import { useRef, useEffect, useState } from "react";
import { motion, useMotionValue } from "framer-motion";

/**
 * WorkSlider — Dynamic infinite video marquee powered by Cloudflare R2
 *
 * HOW TO ADD MORE VIDEOS (no code changes needed):
 * 1. Upload the .mp4 to R2 bucket under the Videos/ folder.
 * 2. Edit videos-manifest.json in the R2 bucket ROOT — add an entry:
 *    { "filename": "Your Video.mp4", "title": "Name or empty string", "reelUrl": "https://..." }
 * 3. Re-upload videos-manifest.json to R2 root. Done.
 */

const R2_BASE      = "https://pub-28b8a924307643ad8891752dc076baa6.r2.dev";
const VIDEOS_URL   = `${R2_BASE}/Videos`;
const MANIFEST_URL = `${R2_BASE}/videos-manifest.json`;

const BASE_DURATION  = 28;
const HOVER_DURATION = BASE_DURATION * 2.8;
const MIN_CARDS      = 6;

function padList(arr) {
  if (arr.length === 0) return [];
  if (arr.length >= MIN_CARDS) return arr;
  const copies = Math.ceil(MIN_CARDS / arr.length);
  return Array.from({ length: copies }, () => arr).flat();
}

// ─── VideoCard ───────────────────────────────────────────────────────────────

function VideoCard({ video }) {
  const wrapperRef = useRef(null);
  const videoRef   = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [failed,  setFailed]  = useState(false); // codec / load error

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const el      = videoRef.current;
    if (!wrapper || !el) return;

    const onError = () => setFailed(true);
    el.addEventListener("error", onError);

    let visible = false;

    const tryPlay = () => {
      if (!visible) return;
      el.play().then(() => setPlaying(true)).catch(() => {});
    };

    // canplay fires when browser has enough data to start playing
    el.addEventListener("canplay", tryPlay);

    const observer = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        if (visible) {
          // If already has data, play now; otherwise canplay listener will trigger it
          if (el.readyState >= 3) {
            tryPlay();
          }
        } else {
          el.pause();
          setPlaying(false);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(wrapper);

    return () => {
      observer.disconnect();
      el.removeEventListener("error", onError);
      el.removeEventListener("canplay", tryPlay);
    };
  }, []);

  return (
    <motion.div
      className="relative flex-shrink-0 w-[200px] sm:w-[220px] md:w-[240px] select-none"
      style={{ aspectRatio: "9 / 16" }}
      whileHover={{
        scale: 1.06,
        transition: { duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] },
      }}
    >
      <div
        ref={wrapperRef}
        className="absolute inset-0 rounded-2xl overflow-hidden bg-zinc-900"
        style={{ transform: "translate3d(0,0,0)" }}
      >
        {/* shimmer while buffering — hidden once playing or failed */}
        {!playing && !failed && (
          <div className="absolute inset-0 bg-zinc-800 animate-pulse rounded-2xl" />
        )}

        {/* codec/load error state */}
        {failed && (
          <div className="absolute inset-0 bg-zinc-900 flex flex-col items-center justify-center gap-2 rounded-2xl">
            <span className="text-zinc-500 text-2xl">⚠</span>
            <p className="text-zinc-600 text-xs font-mono text-center px-4">
              Unsupported format.<br />Re-encode to H.264.
            </p>
          </div>
        )}

        {/*
          src set directly in JSX — browser fetches first frame via preload="metadata".
          Full video streams only when play() is called. Safe for 90+ MB files.
        */}
        <video
          ref={videoRef}
          src={video.src}
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 h-full w-full object-cover"
          style={{ transform: "translate3d(0,0,0)" }}
        />

        {/* gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/10 pointer-events-none" />

        {/* title — only shown when non-empty */}
        {video.title && (
          <div className="absolute bottom-3 left-3 right-3 pointer-events-none">
            <p className="text-white/80 text-xs font-mono uppercase tracking-widest truncate">
              {video.title}
            </p>
          </div>
        )}


      </div>
    </motion.div>
  );
}

// ─── WorkSlider ──────────────────────────────────────────────────────────────

export default function WorkSlider({ displayHeading = false }) {
  const [videos,     setVideos]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Try R2 manifest first
        const res = await fetch(MANIFEST_URL, { cache: "no-cache" });
        if (!res.ok) throw new Error(`${res.status}`);
        const data = await res.json();
        if (!cancelled) {
          const list = (data.videos || []).map((v) => ({
            src:     `${VIDEOS_URL}/${encodeURIComponent(v.filename)}`,
            title:   v.title ?? "",
            reelUrl: v.reelUrl || "https://www.instagram.com/hotsaucelab/",
          }));
          setVideos(list);
          setLoading(false);
        }
      } catch (err) {
        // R2 failed (likely CORS on localhost) — fall back to local manifest
        try {
          const res = await fetch("/videos-manifest.json", { cache: "no-cache" });
          if (!res.ok) throw new Error("local manifest missing");
          const data = await res.json();
          if (!cancelled) {
            const list = (data.videos || []).map((v) => ({
              // local manifest → use R2 URLs (videos are only on R2)
              src:     `${VIDEOS_URL}/${encodeURIComponent(v.filename)}`,
              title:   v.title ?? "",
              reelUrl: v.reelUrl || "https://www.instagram.com/hotsaucelab/",
            }));
            setVideos(list);
            setLoading(false);
          }
        } catch {
          if (!cancelled) {
            console.error("[WorkSlider] both manifest sources failed");
            setFetchError(true);
            setLoading(false);
          }
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const trackRef     = useRef(null);
  const [isHovered,  setIsHovered]  = useState(false);
  const [cardWidth,  setCardWidth]  = useState(240);
  const GAP = 24;

  const paddedVideos   = padList(videos);
  const loopVideos     = [...paddedVideos, ...paddedVideos];
  const singleSetWidth = paddedVideos.length * (cardWidth + GAP);

  const x            = useMotionValue(0);
  const startTimeRef = useRef(null);
  const pausedAtRef  = useRef(0);
  const isHoveredRef = useRef(false);

  useEffect(() => { isHoveredRef.current = isHovered; }, [isHovered]);

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      setCardWidth(w < 640 ? 200 : w < 768 ? 220 : 240);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  useEffect(() => {
    if (paddedVideos.length === 0) return;
    let raf;
    const speed      = singleSetWidth / (BASE_DURATION  * 1000);
    const hoverSpeed = singleSetWidth / (HOVER_DURATION * 1000);

    function animate(ts) {
      if (!startTimeRef.current) startTimeRef.current = ts - pausedAtRef.current;
      const elapsed  = ts - startTimeRef.current;
      const spd      = isHoveredRef.current ? hoverSpeed : speed;
      const rawX     = -(elapsed * spd);
      const wrappedX = ((rawX % singleSetWidth) - singleSetWidth) % -singleSetWidth;
      x.set(wrappedX > 0 ? wrappedX - singleSetWidth : wrappedX);
      raf = requestAnimationFrame(animate);
    }

    raf = requestAnimationFrame(animate);
    return () => {
      cancelAnimationFrame(raf);
      pausedAtRef.current  = Math.abs(x.get());
      startTimeRef.current = null;
    };
  }, [singleSetWidth, x, paddedVideos.length]);

  const BG = "#e8e8e8";

  if (loading) {
    return (
      <section
        className="py-10 md:py-16 overflow-hidden border-t border-b border-zinc-900"
        style={{ backgroundColor: BG }}
      >
        <p className="font-mono text-2xl font-bold uppercase tracking-[0.3em] text-zinc-500 px-5 md:px-10 mb-8">
          Recent work
        </p>
        <div className="flex gap-6 px-10 overflow-hidden">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="flex-shrink-0 w-[200px] sm:w-[220px] md:w-[240px] rounded-2xl bg-zinc-300 animate-pulse"
              style={{ aspectRatio: "9 / 16" }}
            />
          ))}
        </div>
      </section>
    );
  }

  if (fetchError || videos.length === 0) {
    return (
      <section
        className="py-10 md:py-16 overflow-hidden border-t border-b border-zinc-900"
        style={{ backgroundColor: BG }}
      >
        <p className="font-mono text-2xl font-bold uppercase tracking-[0.3em] text-zinc-500 px-5 md:px-10 mb-4">
          Recent work
        </p>
        <p className="font-mono text-sm text-zinc-400 px-5 md:px-10">
          {fetchError ? "Could not load videos — check back soon." : "No videos yet."}
        </p>
      </section>
    );
  }

  return (
    <section
      className="py-10 md:py-16 overflow-hidden border-t border-b border-zinc-900"
      style={{ backgroundColor: BG }}
      aria-label="Work showcase"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {displayHeading ? (
        <h2 className="font-display uppercase text-5xl md:text-6xl lg:text-7xl leading-none text-zinc-900 px-5 md:px-10 mb-8">
          Recent work
        </h2>
      ) : (
        <p className="font-mono text-2xl font-bold uppercase tracking-[0.3em] text-zinc-500 px-5 md:px-10 mb-8">
          Recent work
        </p>
      )}

      <div className="relative overflow-hidden" style={{ backgroundColor: BG }}>
        <div
          className="pointer-events-none absolute inset-y-0 left-0 w-16 md:w-24 z-10"
          style={{ background: `linear-gradient(to right, ${BG}, transparent)` }}
        />
        <div
          className="pointer-events-none absolute inset-y-0 right-0 w-16 md:w-24 z-10"
          style={{ background: `linear-gradient(to left, ${BG}, transparent)` }}
        />

        <motion.div
          ref={trackRef}
          style={{
            x,
            display: "flex",
            gap: `${GAP}px`,
            width: "max-content",
            backgroundColor: BG,
            paddingTop: "12px",
            paddingBottom: "12px",
            transform: "translate3d(0,0,0)",
            willChange: "transform",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
          }}
        >
          {loopVideos.map((video, i) => (
            <VideoCard key={`${video.src}-${i}`} video={video} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}
